// Share links: /s?v=<videoId>&t=<start>&l=<target>&s=<mother>&c=<score>&by=<name>&q=<sentence>&a=<audioId>
// The sentence rides in the URL so the link-preview Function needn't duplicate the splitter.
// Imported by the app (Babel 6) AND the preview Function: no `?.`, `??` or `\p{}`
// (Jest's Babel 7 won't catch it), no browser globals or process.env at module scope.

export const SHARE_PATH = '/s';

// Long enough for real names, too short to be a billboard.
export const MAX_NAME_LEN = 24;

export const MAX_QUOTE_LEN = 160;

const VIDEO_ID_RE = /^[\w-]{11}$/;
const LANG_CODE_RE = /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/i;

// Matches the id minted by the Worker's POST /v1/share/audio.
const AUDIO_ID_RE = /^[A-Za-z0-9_-]{22}$/;

// Explicit ranges (Babel 6 mangles \p{}). Values end up in preview HTML attributes.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\u0000-\u001F\u007F]/g;
const WHITESPACE_RUN_RE = /\s+/g;

/** First token only: a full name should not end up in pasted links. */
export function firstName(fullName) {
  const clean = collapse(fullName);
  if (!clean) return '';
  return clean.split(' ')[0].slice(0, MAX_NAME_LEN);
}

export function collapse(raw) {
  if (typeof raw !== 'string') return '';
  return raw.replace(CONTROL_CHARS_RE, ' ').replace(WHITESPACE_RUN_RE, ' ').trim();
}

export function clampQuote(raw) {
  const clean = collapse(raw);
  if (clean.length <= MAX_QUOTE_LEN) return clean;
  return clean.slice(0, MAX_QUOTE_LEN - 1).trim() + '…';
}

// Also encodes ' ( ) ! *: chat auto-linkers often end the URL there.
function encodeParam(value) {
  return encodeURIComponent(value).replace(/['()!*]/g, function (c) {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase();
  });
}

function validLang(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return LANG_CODE_RE.test(s) ? s.toLowerCase() : null;
}

function validScore(raw) {
  // Number('') is 0, so without this a missing score reads as 0%.
  const s = raw === null || raw === undefined ? '' : String(raw).trim();
  if (!s) return null;
  const n = Math.round(Number(s));
  if (!isFinite(n) || n < 0 || n > 100) return null;
  return n;
}

// vue-router gives an array for a repeated param; take the first.
function firstValue(v) {
  if (Array.isArray(v)) return v.length ? v[0] : '';
  return v === undefined || v === null ? '' : String(v);
}

// start stays a float: truncating it lands on the previous sentence.
export function buildShareUrl(payload, origin) {
  const p = payload || {};
  if (!VIDEO_ID_RE.test(String(p.videoId || ''))) return null;

  let base = origin;
  if (!base && typeof window !== 'undefined' && window.location) {
    base = window.location.origin;
  }

  const parts = ['v=' + encodeURIComponent(p.videoId)];

  const start = Number(p.start);
  parts.push('t=' + (isFinite(start) && start > 0 ? String(start) : '0'));

  const lang = validLang(p.lang);
  if (lang) parts.push('l=' + encodeURIComponent(lang));

  const mother = validLang(p.motherTongue);
  if (mother) parts.push('s=' + encodeURIComponent(mother));

  const score = validScore(p.score);
  if (score !== null) parts.push('c=' + score);

  const name = firstName(p.name);
  if (name) parts.push('by=' + encodeParam(name));

  const sentence = clampQuote(p.sentence);
  if (sentence) parts.push('q=' + encodeParam(sentence));

  if (AUDIO_ID_RE.test(String(p.audioId || ''))) {
    parts.push('a=' + encodeURIComponent(p.audioId));
  }

  return (base || '') + SHARE_PATH + '?' + parts.join('&');
}

// Fields validate independently; only a bad `v` returns null.
export function parseShareParams(query) {
  const q = query || {};
  const videoId = firstValue(q.v).trim();
  if (!VIDEO_ID_RE.test(videoId)) return null;

  const start = parseFloat(firstValue(q.t));
  const name = firstName(firstValue(q.by));
  const sentence = clampQuote(firstValue(q.q));
  const audioId = firstValue(q.a).trim();

  return {
    videoId: videoId,
    start: isFinite(start) && start > 0 ? start : 0,
    lang: validLang(firstValue(q.l)),
    motherTongue: validLang(firstValue(q.s)),
    score: validScore(firstValue(q.c)),
    name: name || null,
    sentence: sentence || null,
    audioId: AUDIO_ID_RE.test(audioId) ? audioId : null
  };
}

// Not v/t: normal navigation and resume links carry those too.
const SHARE_ONLY_PARAMS = ['l', 's', 'c', 'by', 'q', 'a'];

/** True only for real share links, so onboarding isn't skipped for normal visits. */
export function hasShareParams(query) {
  const q = query || {};
  for (let i = 0; i < SHARE_ONLY_PARAMS.length; i++) {
    if (firstValue(q[SHARE_ONLY_PARAMS[i]]).trim()) return true;
  }
  return false;
}

/** A link without a score is a "practise with me" link, not a challenge. */
export function isChallenge(payload) {
  return !!(payload && payload.score !== null && payload.score !== undefined);
}

export function shareText(payload) {
  const p = payload || {};
  const quote = clampQuote(p.sentence);
  const score = validScore(p.score);

  if (score !== null) {
    return quote
      ? 'I scored ' + score + '% saying this line: "' + quote + '" Can you beat me?'
      : 'I scored ' + score + '% on this line. Can you beat me?';
  }
  return quote
    ? 'Try saying this line with me: "' + quote + '"'
    : 'Practise this clip with me on EZ Shadow.';
}

export function shareTitle(payload) {
  return isChallenge(payload) ? 'Beat my score on EZ Shadow' : 'Shadow this clip with me';
}

/** Resolves true on success. execCommand fallback covers http:// and older WebKit. */
export function copyToClipboard(text) {
  const value = String(text == null ? '' : text);

  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(value).then(
      () => true,
      () => legacyCopy(value)
    );
  }
  return Promise.resolve(legacyCopy(value));
}

function legacyCopy(value) {
  if (typeof document === 'undefined' || !document.body) return false;
  const el = document.createElement('textarea');
  el.value = value;
  // Off-screen (display:none can't be selected); readonly avoids the mobile keyboard.
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  let ok = false;
  try {
    el.select();
    ok = document.execCommand('copy');
  } catch (e) {
    ok = false;
  }
  document.body.removeChild(el);
  return ok;
}
